import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../utils/toast';
import './AdminTable.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.privemobility.com/api';

const GuaranteesAdmin = () => {
  const [guarantees, setGuarantees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGuarantee, setEditingGuarantee] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadGuarantees = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/guarantees`);
      setGuarantees((response.data || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('Error loading guarantees:', error);
      toast.error('Error al cargar garantías');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGuarantees();
  }, [loadGuarantees]);

  const handleEdit = (guarantee) => {
    setEditingGuarantee(guarantee);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingGuarantee(null);
    setShowForm(true);
  };

  const handleSave = async (guaranteeData) => {
    try {
      if (editingGuarantee) {
        await axios.put(`${API_URL}/guarantees/${editingGuarantee.id}`, guaranteeData);
        toast.success('Garantía actualizada exitosamente');
      } else {
        await axios.post(`${API_URL}/guarantees`, guaranteeData);
        toast.success('Garantía creada exitosamente');
      }
      setShowForm(false);
      setEditingGuarantee(null);
      loadGuarantees();
    } catch (error) {
      console.error('Error saving guarantee:', error);
      toast.error('Error al guardar garantía');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta garantía?')) return;

    try {
      await axios.delete(`${API_URL}/guarantees/${id}`);
      toast.success('Garantía eliminada exitosamente');
      loadGuarantees();
    } catch (error) {
      console.error('Error deleting guarantee:', error);
      toast.error('Error al eliminar garantía');
    }
  };

  if (loading) {
    return <div className="admin-loading">Cargando garantías...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gestión de Garantías</h2>
        <button className="btn-primary" onClick={handleCreate}>
          + Nueva Garantía
        </button>
      </div>

      {showForm && (
        <GuaranteeForm
          guarantee={editingGuarantee}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingGuarantee(null);
          }}
        />
      )}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Orden</th>
              <th>Texto</th>
              <th>Valor</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {guarantees.map(guarantee => (
              <tr key={guarantee.id}>
                <td>{guarantee.order}</td>
                <td>{guarantee.text}</td>
                <td><strong>{guarantee.value}</strong></td>
                <td>
                  <span className={`status ${guarantee.active ? 'active' : 'inactive'}`}>
                    {guarantee.active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(guarantee)}>
                      ✏️
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(guarantee.id)}>
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {guarantees.length === 0 && (
          <div className="empty-state">
            No hay garantías registradas. Crea una nueva para comenzar.
          </div>
        )}
      </div>
    </div>
  );
};

const GuaranteeForm = ({ guarantee, onSave, onCancel }) => {
  const [formData, setFormData] = useState(guarantee || {
    id: '',
    text: '',
    value: '',
    order: 1,
    active: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="form-modal">
      <div className="form-content">
        <h3>{guarantee ? 'Editar Garantía' : 'Nueva Garantía'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>ID *</label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                required
                disabled={!!guarantee}
              />
            </div>

            <div className="form-group">
              <label>Orden *</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Texto *</label>
              <input
                type="text"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                required
                placeholder="Ej: Garantía, Franquicia por daños"
              />
            </div>

            <div className="form-group full-width">
              <label>Valor *</label>
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                required
                placeholder="Ej: $ 350.000"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span>Activo</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuaranteesAdmin;
